import { useQueries } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import type { PaginationState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Avatar } from "@/components/base/avatar/avatar";
import { TabHeader } from "@/components/base/tab-header";
import { EmptyValue } from "@/components/common";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { organizationQueryOptions } from "@/services/organization";
import { getUserInitials } from "@/utils/get-user-initials";

// Combined data access user type for display
interface DataAccessUserDisplay {
	id: string;
	userId: string;
	organizationUserId: string;
	firstName: string;
	lastName: string;
	email: string;
	belongsToOrganization: string;
	role: string;
	fullAccess: boolean;
	hasAvatar: boolean;
}

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations/(sub-orgs)/$subOrgId/data-access",
)({
	component: RouteComponent,
});

// Avatar cell component - extracted to properly use hooks
function UserAvatarCell({ user }: { user: DataAccessUserDisplay }) {
	const { src: userAvatarSrc, isLoading } = useUserAvatarSrc(
		user.userId,
		user.hasAvatar,
	);

	const firstName = user.firstName;
	const lastName = user.lastName;
	const fullName = `${firstName} ${lastName}`;

	// Only show initials when not loading and no avatar
	const initials =
		!isLoading && !userAvatarSrc
			? getUserInitials(firstName, lastName)
			: undefined;

	return (
		<div className="flex items-center gap-3">
			<Avatar size="md" src={userAvatarSrc || undefined} initials={initials} />
			<div className="flex flex-col">
				<span className="text-sm font-semibold text-primary">{fullName}</span>
				<span className="text-sm text-tertiary">{user.email}</span>
			</div>
		</div>
	);
}

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations/(sub-orgs)/$subOrgId/data-access",
	});

	// Fetch data access entries and organization details in parallel
	const [dataAccessEntries, organizationQuery] = useQueries({
		queries: [
			organizationQueryOptions.organizationDataAccess.list(
				params.orgId,
				1,
				100, // Fetch up to 100 data access entries
				{
					subOrganizationId: params.subOrgId,
				},
			),
			organizationQueryOptions.detail(params.orgId),
		],
	});

	// Fetch user details for each data access entry (depends on dataAccessEntries)
	const dataAccessUsers = useQueries({
		queries:
			dataAccessEntries.data?.map((dataAccessEntry) => {
				return organizationQueryOptions.users.detail(
					params.orgId,
					dataAccessEntry.organizationUserId ?? "",
				);
			}) ?? [],
		combine: (results) => {
			const entries = dataAccessEntries.data ?? [];
			return {
				data: results
					.map((result, index) => {
						const entry = entries[index];
						const user = result.data;
						if (!user || !entry) return null;
						return {
							id: user.id ?? "",
							userId: user.userId ?? "",
							organizationUserId: entry.organizationUserId ?? "",
							firstName: user.firstName ?? "",
							lastName: user.lastName ?? "",
							email: user.email ?? "",
							belongsToOrganization: user.belongsTo ?? "",
							role: user.organizationRoles?.[0]?.name ?? "",
							fullAccess: entry.fullAccess ?? false,
							hasAvatar: user.hasAvatar === true,
						} satisfies DataAccessUserDisplay;
					})
					.filter((item): item is DataAccessUserDisplay => item !== null),
				// Include parent query states to properly track loading across all queries
				isPending:
					dataAccessEntries.isPending ||
					organizationQuery.isPending ||
					results.some((r) => r.isPending),
				isError:
					dataAccessEntries.isError ||
					organizationQuery.isError ||
					results.some((r) => r.isError),
			};
		},
	});

	const dataAccessColumns: ColumnDef<DataAccessUserDisplay>[] = [
		{
			id: "nameEmail",
			accessorFn: (row) =>
				`${row.lastName ?? ""} ${row.firstName ?? ""} ${row.email ?? ""}`
					.trim()
					.toLowerCase(),
			header: t(
				"org-admin.sub-organizations.tabs.data-access.columns.nameEmail",
			),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			minSize: 300,
			cell: ({ row }) => <UserAvatarCell user={row.original} />,
		},
		{
			id: "belongsTo",
			accessorFn: (row) => row.belongsToOrganization?.toLowerCase() ?? "",
			header: t(
				"org-admin.sub-organizations.tabs.data-access.columns.belongsTo",
			),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				return row.original.belongsToOrganization ? (
					<span className="text-sm text-tertiary">
						{row.original.belongsToOrganization}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "role",
			accessorFn: (row) => row.role?.toLowerCase() ?? "",
			header: t("org-admin.sub-organizations.tabs.data-access.columns.role"),
			enableSorting: true,
			minSize: 150,
			cell: ({ row }) => {
				return row.original.role ? (
					<span className="text-sm text-tertiary">{row.original.role}</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "dataAccess",
			accessorFn: (row) => (row.fullAccess ? "All" : "Assigned"),
			header: t(
				"org-admin.sub-organizations.tabs.data-access.columns.dataAccess",
			),
			enableSorting: true,
			minSize: 150,
			cell: ({ row }) => (
				<span className="text-sm text-secondary">
					{row.original.fullAccess
						? t("common.buttonGroup.all")
						: t("common.buttonGroup.assigned")}
				</span>
			),
		},
	];

	const data = dataAccessUsers.data ?? [];

	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});

	const pageRowCount = useMemo(() => {
		const start = pagination.pageIndex * pagination.pageSize;
		return Math.min(pagination.pageSize, Math.max(0, data.length - start));
	}, [data.length, pagination.pageIndex, pagination.pageSize]);

	const hideTableCard = !dataAccessUsers.isPending && data.length === 0;

	return (
		<div>
			<TabHeader
				title={t("org-admin.sub-organizations.tabs.data-access.title")}
				descriptionText={t(
					"org-admin.sub-organizations.tabs.dataAccess.description",
				)}
			/>

			<CustomTableWrapper
				data={data}
				columns={dataAccessColumns}
				isLoading={dataAccessUsers.isPending}
				isFetching={dataAccessUsers.isPending}
				enableSorting={true}
				paginationMode="client"
				externalPaginationState={pagination}
				onPaginationChange={setPagination}
				rowCount={pageRowCount}
				totalRowCount={data.length}
				className="overflow-x-auto overflow-y-visible"
				cardClassName={hideTableCard ? "hidden" : undefined}
				zebraStriping
				showHeaderPadding={false}
				emptyState={
					<div className="flex flex-col items-center justify-center py-12">
						<p className="text-sm text-tertiary">
							{t("org-admin.sub-organizations.tabs.data-access.noUsers")}
						</p>
					</div>
				}
			/>
		</div>
	);
}
